import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import {
  Button,
  FormHelperText,
  Grid,
  Typography,
} from '@mui/material';
import React, { ChangeEvent, DragEvent, useCallback, useRef, useState } from 'react';
import { useDropArea } from 'react-use';

interface FileTabContentProps {
  dropAreaNote?: string;
  accept?: string;
  onSubmit: (value: File) => void;
}

export const FileTabContent: React.FC<FileTabContentProps> = ({
  dropAreaNote,
  accept,
  onSubmit,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectFiles = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const changeFile = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;

      if (files) {
        const value = Array.from(files)[0];
        onSubmit(value);
      }
    },
    [onSubmit],
  );

  const dragFile = useCallback(
    (files: File[]) => {
      if (files) {
        const value = Array.from(files)[0];

        if (accept) {
          if (accept.includes(value.type)) {
            setError(null);
          } else {
            setError('Invalid file type');
            return;
          }
        }

        return onSubmit(value);
      }
    },
    [accept, onSubmit],
  );

  const [bond, state] = useDropArea({
    onFiles: dragFile,
  });

  return (
    <>
      <Grid
        {...bond}
        onDragLeave={(event: DragEvent<HTMLElement>) => {
          if (!(event.relatedTarget instanceof Node)) return;
          if (event.currentTarget.contains(event.relatedTarget)) return;
          bond.onDragLeave(event);
        }}
        container
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: '20px',
          gap: '20px',
          border: (theme) => `2px dashed ${state.over ? theme.palette.success.main : theme.palette.grey[200]}`,
          borderRadius: 0.5,
          backgroundColor: 'grey.50',
          color: 'grey.400',
          transition: 'border .24s ease-in-out',
        }}
      >
        <Grid item>
          <Typography variant="body2">Drag and drop file here, or click to select file</Typography>
        </Grid>

        {!!dropAreaNote && (
          <Grid item>
            <Typography variant="body2">{dropAreaNote}</Typography>
          </Grid>
        )}

        <input
          ref={inputRef}
          accept={accept}
          multiple={false}
          onChange={changeFile}
          type="file"
          hidden
        />

        <Grid item>
          <Button
            startIcon={<CloudUploadIcon />}
            onClick={selectFiles}
            variant="contained"
            fullWidth
          >
            Upload
          </Button>
        </Grid>
      </Grid>

      {error && <FormHelperText error>{error}</FormHelperText>}
    </>
  );
}; 